import {createGate} from 'effector-react';
import i18next from 'i18next';

import {toast} from 'react-toastify';

import {
    fetchDocumentCommissioningCreate,
    fetchDocumentDropOutCreate,
    fetchDocumentGtinInfo,
    fetchDocumentMovingCreate,
    fetchDocumentReturnGoodsCreate,
    fetchDocumentsGrid,
    fetchDocumentUPDCreate,
} from 'api/documents';
import {fetchDocumentUtilisationCreate} from 'api/documents/utilisation';
import {history} from 'core/history';
import {rootDomain} from 'core/rootDomain';
import {ROUTE} from 'core/routes';
import {CommissioningFormData, DropOutFormData, MovingFormData, UtilisationFormData} from 'types';

import {CodeResponseType} from 'types/validation';

const domain = rootDomain.createDomain('DocumentsListDomain');
export const DocumentsCreateGate = createGate('DocumentsCreateGate');
export const DocumentCreatePreviewGate = createGate('DocumentCreatePreviewGate');

export const sendDropOutFx = domain.createEffect({
    name: 'sendDropOutFx',
    handler: fetchDocumentDropOutCreate,
});
export const sendUtilisationFx = domain.createEffect({
    name: 'sendUtilisationFx',
    handler: fetchDocumentUtilisationCreate,
});
export const sendCommissioningFx = domain.createEffect({
    name: 'sendCommissioningFx',
    handler: fetchDocumentCommissioningCreate,
});
export const sendUPDFx = domain.createEffect({
    name: 'sendUPDFx',
    handler: fetchDocumentUPDCreate,
});

export const sendMovingFx = domain.createEffect({
    name: 'sendMovingFx',
    handler: fetchDocumentMovingCreate,
});

export const fetchGtinInfoFx = domain.createEffect({
    name: 'fetchGtinInfoFx',
    handler: fetchDocumentGtinInfo,
});

export const createDocumentReturnGoodsFx = domain.createEffect({
    name: 'createDocumentReturnGoodsFx',
    handler: fetchDocumentReturnGoodsCreate,
});

const DOCUMENT_IN_GRID_POLL_INTERVAL_MS = 500;
const DOCUMENT_IN_GRID_MAX_ATTEMPTS = 30;

let documentInGridPollingId = 0;

const isDocumentInGridPollingActive = (pollingId: number) => pollingId === documentInGridPollingId;

export const stopDocumentInGridPolling = domain.createEvent();

stopDocumentInGridPolling.watch(() => {
    documentInGridPollingId += 1;
});

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export type WaitDocumentInGridParams = {
    reportId: string;
    redirectTo?: string;
    updOnly?: boolean;
};

const normalizeWaitDocumentInGridParams = (
    params: string | WaitDocumentInGridParams,
): Required<WaitDocumentInGridParams> => {
    if (typeof params === 'string') {
        return {
            reportId: params,
            redirectTo: ROUTE.DOCUMENTS_DOCUMENTS,
            updOnly: false,
        };
    }

    return {
        reportId: params.reportId,
        redirectTo: params.redirectTo ?? ROUTE.DOCUMENTS_DOCUMENTS,
        updOnly: params.updOnly ?? false,
    };
};

const waitUntilDocumentInGrid = async (
    reportId: string,
    attemptsLeft: number,
    pollingId: number,
    updOnly: boolean,
): Promise<boolean> => {
    if (!isDocumentInGridPollingActive(pollingId)) {
        return false;
    }

    const result = await fetchDocumentsGrid({
        paging: {pageNumber: 0, pageSize: 20},
        filters: [],
        sortings: [{id: 'createdAt', desc: true}],
        updOnly,
        search: reportId,
    });

    if (!isDocumentInGridPollingActive(pollingId)) {
        return false;
    }

    if (
        result.content?.some((doc) => {
            if (updOnly) {
                return doc.reportId === reportId && !!doc.irn;
            }

            return doc.reportId === reportId;
        })
    ) {
        return true;
    }

    if (attemptsLeft <= 1) {
        return true;
    }

    await delay(DOCUMENT_IN_GRID_POLL_INTERVAL_MS);

    if (!isDocumentInGridPollingActive(pollingId)) {
        return false;
    }

    return waitUntilDocumentInGrid(reportId, attemptsLeft - 1, pollingId, updOnly);
};

export const showBatchNumberModal = domain.createEvent<boolean>();
export const batchNumberModal$ = domain.createStore(false);
export const confirmBatchNumberModal = domain.createEvent();

export const waitDocumentInGridAndRedirectFx = domain.createEffect({
    name: 'waitDocumentInGridAndRedirectFx',
    handler: async (params: string | WaitDocumentInGridParams) => {
        const {reportId, redirectTo, updOnly} = normalizeWaitDocumentInGridParams(params);
        const pollingId = ++documentInGridPollingId;

        if (reportId) {
            const shouldRedirect = await waitUntilDocumentInGrid(
                reportId,
                DOCUMENT_IN_GRID_MAX_ATTEMPTS,
                pollingId,
                updOnly,
            );
            if (!shouldRedirect || !isDocumentInGridPollingActive(pollingId)) {
                return;
            }
        } else if (!isDocumentInGridPollingActive(pollingId)) {
            return;
        }

        showBatchNumberModal(false);
        toast.success(i18next.t('pages.documents.form.documentCreated', {number: reportId}), {
            className: 'Toastify__toast__wide',
        });
        history.push(redirectTo);
    },
});

export const createDropOutDocument = domain.createEvent();
export const createUtilisationDocument = domain.createEvent();
export const createCommissioningDocument = domain.createEvent();
export const createMovingDocument = domain.createEvent();

export const dropOutFormChanged = domain.createEvent<DropOutFormData>();
export const dropOutForm$ = domain.createStore<DropOutFormData>({
    documentId: '',
    reason: '',
    salesChannel: '',
    supportingDocumentNumber: '',
    supportingDocumentDate: null,
    comment: '',
    attachmentFiles: [],
    counterpartyId: '',
    counterpartyName: '',
    destinationCountry: '',
    resultMarkingCodes: [],
});

export const isUtilisationGroup$ = domain.createStore(false);
export const utilisationGroupChanged = domain.createEvent<boolean>();
export const utilisationFormChanged = domain.createEvent<Partial<UtilisationFormData>>();
export const utilisationForm$ = domain.createStore<UtilisationFormData>({
    groupMarkingCode: null,
    factoryId: '',
    factoryName: '',
    productionLineId: '',
    productionLineName: '',
    applicationDate: null,
    manufacturingDate: null,
    storageLocationId: null,
    storageLocationName: '',
    batchNumber: '',
    batchNumberSource: 'auto',
});

export const commissioningFormChanged = domain.createEvent<CommissioningFormData>();
export const commissioningForm$ = domain.createStore<CommissioningFormData>({
    startCirculationDate: null,
});

export const movingFormChanged = domain.createEvent<MovingFormData>();
export const movingForm$ = domain.createStore<MovingFormData>({
    employeeFullName: '',
    storageLocationFrom: '',
    storageLocationTo: '',
    storageLocationFromId: null,
    storageLocationToId: null,
    note: '',
});

export const dataChanged = domain.createEvent<CodeResponseType['info'] | null>();
export const data$ = domain.createStore<CodeResponseType['info'][]>([]);

export const step$ = domain.createStore<number>(0);
export const stepChanged = domain.createEvent<number>();

export const fileChanged = domain.createEvent<File | undefined | null>();
export const file$ = domain.createStore<File | undefined | null>(null);

export const codes$ = domain.createStore<Array<string>>([]);
export const codesChanged = domain.createEvent<Array<string>>();

export const gtins$ = domain.createStore<any>({});

export const UPDString$ = domain.createStore<string | null>(null);
export const UPDStringChanged = domain.createEvent<string | null>();

export const lastCreatedDocumentId$ = domain.createStore<string | null>(null);
export const setLastCreatedDocumentId = domain.createEvent<string | null>();

export const lastCreatedInvocieId$ = domain.createStore<string | null>(null);
export const setLastCreatedInvocieId = domain.createEvent<string | null>();
